import React, { useState, useContext } from 'react';
import axios from 'axios';
import { useQuery, useMutation } from '@tanstack/react-query';
import { SessionContext } from '#client/contexts/SessionContext';
import { useToast } from '#client/components/common/Toast/ToastContext';
import { z } from 'zod';

import { Card, IndexTable, PageActions, Select, BlockStack, InlineStack, Form, FormLayout, Modal, Tooltip, TextField, Tag, Combobox, Listbox, Divider, Button, Text } from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { queryClient } from '#client/utils/queryClient';

const formSchema = z.object({
    newUserEmail: z.string().email('Please enter a valid email address.'),
});

const UserManagementTab = () => {
    const { currentUser, refetchUser } = useContext(SessionContext);
    const { addToast } = useToast();

    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserRole, setNewUserRole] = useState('');
    const [newUserLocationIds, setNewUserLocationIds] = useState([]);

    const [locationSearches, setLocationSearches] = useState({});
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [unsavedChanges, setUnsavedChanges] = useState({});

    // Fetch users
    const {
        data: users = [],
        isLoading: isLoadingUsers,
        refetch: refetchUsers,
    } = useQuery({
        queryKey: ['admin-users'],
        queryFn: async () => {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/app/admin/users`);
            return response.data.data.users;
        },
    });

    // Fetch roles
    const { data: roles = [], isLoading: isLoadingRoles } = useQuery({
        queryKey: ['admin-roles'],
        queryFn: async () => {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/app/admin/roles`);
            return response.data.data.roles;
        },
    });

    // Fetch locations
    const { data: locations = [], isLoading: isLoadingLocations } = useQuery({
        queryKey: ['admin-locations'],
        queryFn: async () => {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/app/admin/locations`);
            return response.data.data.locations;
        },
    });

    // Mutation for adding a user
    const addUserMutation = useMutation({
        mutationFn: async ({ email, roleId, locationIds }) => {
            const response = await axios.post(`${import.meta.env.VITE_API_URL}/api/app/admin/users`, {
                email,
                roleId,
                locationIds,
            });
            return response.data;
        },
        onSuccess: () => {
            refetchUsers();
            addToast({ message: `${newUserEmail} added!`, type: 'success' });
            setNewUserEmail('');
            setNewUserLocationIds([]);
        },
        onError: (err) => {
            console.error('Failed to add user:', err);
            const message = err.response?.data?.error.error || 'Failed to add user';
            addToast({ type: 'error', message });
        },
    });

    // Mutation for updating user role
    const updateUserRoleMutation = useMutation({
        mutationFn: async ({ userId, roleId }) => {
            const response = await axios.put(`${import.meta.env.VITE_API_URL}/api/app/admin/users/${userId}/role`, {
                roleId,
            });
            return response.data;
        },
    });

    // Mutation for updating user locations
    const updateUserLocationsMutation = useMutation({
        mutationFn: async ({ userId, locationIds }) => {
            const response = await axios.put(`${import.meta.env.VITE_API_URL}/api/app/admin/users/${userId}/locations`, {
                locationIds: locationIds || [],
            });
            return response.data;
        },
    });

    // Mutation for deleting a user
    const deleteUserMutation = useMutation({
        mutationFn: async (userId) => {
            const response = await axios.delete(`${import.meta.env.VITE_API_URL}/api/app/admin/users/${userId}`);
            return response.data;
        },
        onSuccess: () => {
            refetchUsers();
            addToast({ message: 'User deleted successfully.', type: 'success' });
            setDeleteModalOpen(false);
            setUserToDelete(null);
        },
        onError: (err) => {
            console.error('Failed to delete user:', err);
            const message = err.response?.data?.error.error || 'Failed to delete user';
            addToast({ type: 'error', message });
            setDeleteModalOpen(false);
            setUserToDelete(null);
        },
    });

    const getLocationSearch = (userId) => locationSearches[userId] || '';

    const handleAddUser = async () => {
        if (!newUserEmail) return;

        const result = formSchema.safeParse({
            newUserEmail,
        });

        if (!result.success) {
            const formatted = result.error.format();
            addToast({
                type: 'error',
                message: formatted.newUserEmail?._errors?.[0],
            });
            return;
        }

        addUserMutation.mutate({
            email: newUserEmail,
            roleId: newUserRole,
            locationIds: newUserLocationIds,
        });
    };

    const handleRoleChange = (userId, newRoleId) => {
        setUnsavedChanges((prev) => {
            const previous = prev[userId] || users.find((u) => u.id === userId);
            return {
                ...prev,
                [userId]: {
                    ...previous,
                    roleId: newRoleId,
                },
            };
        });
    };

    const handleLocationChange = (userId, updatedLocationIds) => {
        setUnsavedChanges((prev) => {
            const previous = prev[userId] || users.find((u) => u.id === userId);
            return {
                ...prev,
                [userId]: {
                    ...previous,
                    locationIds: updatedLocationIds,
                },
            };
        });
    };

    const handleLocationSearchChange = (userId, value) => {
        setLocationSearches((prev) => ({
            ...prev,
            [userId]: value,
        }));
    };

    const handleSave = async (user) => {
        try {
            await updateUserRoleMutation.mutateAsync({
                userId: user.id,
                roleId: user.roleId,
            });
            await updateUserLocationsMutation.mutateAsync({
                userId: user.id,
                locationIds: user.locationIds || [],
            });
            queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
            addToast({ message: 'Users saved sucessfully.', type: 'success' });
        } catch (err) {
            console.error('Failed to update user role:', err);
            const message = err.response?.data?.error.error || 'Failed to save role';
            addToast({
                type: 'error',
                message: message,
            });
        }
    };

    const handleDelete = async () => {
        if (!userToDelete) return;
        deleteUserMutation.mutate(userToDelete.id);
    };

    console.log('users', users);
    console.log('isLoadingUsers', isLoadingUsers);
    console.log('roles', roles);
    console.log('isLoadingRoles', isLoadingRoles);
    console.log('locations', locations);
    console.log('isLoadingLocations', isLoadingLocations);

    // Show loading state while data is being fetched
    if (isLoadingUsers || isLoadingRoles || isLoadingLocations) {
        return (
            <Card>
                <BlockStack gap="400">
                    <Text as="p">Loading user management data...</Text>
                </BlockStack>
            </Card>
        );
    }

    const rows = users.map((user) => {
        const effectiveUser = unsavedChanges[user.id] || user;
        const selectedLocationIds = effectiveUser.locationIds || [];
        const searchValue = getLocationSearch(user.id);
        const filteredLocations = locations.filter((loc) => loc.name.toLowerCase().includes(searchValue.toLowerCase()));
        const verticalContent =
            selectedLocationIds.length > 0 ? (
                <InlineStack gap="100">
                    {[...selectedLocationIds]
                        .map((id) => locations.find((l) => l.id === id))
                        .filter(Boolean)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((loc) => (
                            <Tag
                                key={loc.id}
                                onRemove={() =>
                                    handleLocationChange(
                                        user.id,
                                        selectedLocationIds.filter((x) => x !== loc.id)
                                    )
                                }
                            >
                                {loc.name}
                            </Tag>
                        ))}
                </InlineStack>
            ) : null;

        return (
            <IndexTable.Row id={user.id} key={user.id}>
                <IndexTable.Cell>{user.email}</IndexTable.Cell>
                <IndexTable.Cell>
                    <Select
                        options={roles.map((role) => ({ label: role.roleName, value: role.roleId }))}
                        value={unsavedChanges[user.id]?.roleId ?? user.roleId}
                        onChange={(value) => handleRoleChange(user.id, value)}
                        disabled={Number(user.id) === currentUser?.id}
                    />
                </IndexTable.Cell>
                <IndexTable.Cell className="location-cell">
                    <BlockStack gap="100">
                        <InlineStack gap="100"></InlineStack>
                        <Combobox
                            allowMultiple
                            activator={
                                <Combobox.TextField
                                    onChange={(value) => handleLocationSearchChange(user.id, value)}
                                    value={searchValue}
                                    autoComplete="off"
                                    verticalContent={verticalContent}
                                    placeholder="Select location(s)"
                                />
                            }
                        >
                            <Listbox
                                onSelect={(value) => {
                                    if (value === 'ALL') {
                                        const allIds = locations.map((loc) => loc.id);
                                        const isAllSelected = selectedLocationIds.length === locations.length;
                                        const updated = isAllSelected ? [] : allIds;
                                        handleLocationChange(user.id, updated);
                                    } else {
                                        const alreadySelected = selectedLocationIds.includes(value);
                                        const updated = alreadySelected ? selectedLocationIds.filter((id) => id !== value) : [...selectedLocationIds, value];
                                        handleLocationChange(user.id, updated);
                                    }

                                    setLocationSearches((prev) => ({ ...prev, [user.id]: '' }));
                                }}
                            >
                                <Listbox.Option key="ALL" value="ALL" selected={selectedLocationIds.length === locations.length}>
                                    {selectedLocationIds.length === locations.length ? 'Deselect All' : 'Select All'}
                                </Listbox.Option>
                                {filteredLocations.map((loc) => (
                                    <Listbox.Option key={loc.id} value={loc.id} selected={selectedLocationIds.includes(loc.id)}>
                                        {loc.name}
                                    </Listbox.Option>
                                ))}
                            </Listbox>
                        </Combobox>
                    </BlockStack>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Tooltip content="Delete User">
                        <Button
                            icon={DeleteIcon}
                            onClick={() => {
                                setUserToDelete(user);
                                setDeleteModalOpen(true);
                            }}
                            plain
                            accessibilityLabel="Delete"
                            disabled={Number(user.id) === currentUser?.id}
                        />
                    </Tooltip>
                </IndexTable.Cell>
            </IndexTable.Row>
        );
    });

    return (
        <Card>
            <Text as="h2" variant="headingMd">
                Add User
            </Text>
            <Card title="Add New User">
                <Form onSubmit={handleAddUser}>
                    <FormLayout>
                        <FormLayout.Group>
                            <TextField value={newUserEmail} onChange={(value) => setNewUserEmail(value)} label="Email" type="email" autoComplete="email"></TextField>
                            <Select
                                label="Role"
                                options={[
                                    { label: 'Select Role', value: '', disabled: true },
                                    ...roles.map((role) => ({
                                        label: role.roleName,
                                        value: role.roleId,
                                    })),
                                ]}
                                value={newUserRole}
                                onChange={(value) => setNewUserRole(value)}
                            />
                            <BlockStack gap="100">
                                <Combobox
                                    allowMultiple
                                    activator={
                                        <Combobox.TextField
                                            label="Locations"
                                            value={getLocationSearch('new')}
                                            onChange={(value) => setLocationSearches((prev) => ({ ...prev, new: value }))}
                                            autoComplete="off"
                                            verticalContent={
                                                <InlineStack gap="100">
                                                    {newUserLocationIds.map((id) => {
                                                        const loc = locations.find((l) => l.id === id);
                                                        return loc ? (
                                                            <Tag key={id} onRemove={() => setNewUserLocationIds((prev) => prev.filter((x) => x !== id))}>
                                                                {loc.name}
                                                            </Tag>
                                                        ) : null;
                                                    })}
                                                </InlineStack>
                                            }
                                            placeholder="Select location(s)"
                                        />
                                    }
                                >
                                    <Listbox
                                        onSelect={(value) => {
                                            if (value === 'ALL') {
                                                const allIds = locations.map((loc) => loc.id);
                                                const isAllSelected = newUserLocationIds.length === locations.length;
                                                setNewUserLocationIds(isAllSelected ? [] : allIds);
                                            } else {
                                                const alreadySelected = newUserLocationIds.includes(value);
                                                const updated = alreadySelected ? newUserLocationIds.filter((id) => id !== value) : [...newUserLocationIds, value];
                                                setNewUserLocationIds(updated);
                                            }

                                            setLocationSearches('');
                                        }}
                                    >
                                        <Listbox.Option key="ALL" value="ALL" selected={newUserLocationIds.length === locations.length}>
                                            {newUserLocationIds.length === locations.length ? 'Deselect All' : 'Select All'}
                                        </Listbox.Option>
                                        {locations
                                            .filter((loc) => loc.name.toLowerCase().includes(getLocationSearch('new').toLowerCase()))
                                            .map((loc) => (
                                                <Listbox.Option key={loc.id} value={loc.id} selected={newUserLocationIds.includes(loc.id)}>
                                                    {loc.name}
                                                </Listbox.Option>
                                            ))}
                                    </Listbox>
                                </Combobox>
                            </BlockStack>
                        </FormLayout.Group>
                        <PageActions
                            primaryAction={
                                <Button submit primary>
                                    Add User
                                </Button>
                            }
                        ></PageActions>
                    </FormLayout>
                </Form>
            </Card>
            <BlockStack gap="500">
                <Divider />
                <Text as="h2" variant="headingMd">
                    User Management
                </Text>

                <IndexTable itemCount={users.length} headings={[{ title: 'Email' }, { title: 'Role' }, { title: 'Locations' }, { title: 'Actions' }]} selectable={false}>
                    {rows}
                </IndexTable>
            </BlockStack>
            <PageActions
                primaryAction={
                    <Button
                        primary
                        disabled={Object.keys(unsavedChanges).length === 0}
                        onClick={async () => {
                            // Track if we need to refresh the current user
                            let shouldRefreshCurrentUser = false;

                            // Loop through all users with unsaved changes
                            for (const userId in unsavedChanges) {
                                const user = unsavedChanges[userId];
                                await handleSave(user);

                                // If we're saving changes for the current user, refresh them
                                if (String(userId) === String(currentUser?.id)) {
                                    shouldRefreshCurrentUser = true;
                                }
                            }

                            // Clear unsaved changes and refetch data
                            setUnsavedChanges({});
                            await refetchUsers();

                            // Refresh the session data if the current user was updated
                            // so location switcher reflects the latest data
                            if (shouldRefreshCurrentUser) {
                                await refetchUser();
                            }
                        }}
                    >
                        Save All
                    </Button>
                }
            ></PageActions>
            <Modal
                open={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setUserToDelete(null);
                }}
                title="Delete API User"
                primaryAction={{
                    content: 'Delete',
                    onAction: handleDelete,
                    destructive: true,
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: () => {
                            setDeleteModalOpen(false);
                            setUserToDelete(null);
                        },
                    },
                ]}
            >
                <Modal.Section>
                    <Text as="p">Are you sure you want to delete the user &rdquo;{userToDelete?.email}&rdquo;?</Text>
                </Modal.Section>
            </Modal>
        </Card>
    );
};

export default UserManagementTab;
